import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Eye, X, User, Calendar, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ANIMALS_EMOJI = {
    'Rat': '鼠', 'Ox': '丑', 'Tiger': '寅', 'Rabbit': '卯',
    'Dragon': '辰', 'Snake': '巳', 'Horse': '午', 'Goat': '未',
    'Monkey': '申', 'Rooster': '酉', 'Dog': '戌', 'Pig': '亥'
};

function AddGuestModal({ onClose, onSave }) {
    const [form, setForm] = useState({
        name: '', birth_year: '', birth_month: '', birth_day: '',
        birth_hour: '', gender: ''
    });
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.birth_year || !form.birth_month || !form.birth_day) {
            toast.error('Date of birth is required');
            return;
        }
        setSaving(true);
        const payload = {
            name: form.name || undefined,
            birth_year: parseInt(form.birth_year),
            birth_month: parseInt(form.birth_month),
            birth_day: parseInt(form.birth_day),
            birth_hour: form.birth_hour ? parseInt(form.birth_hour) : undefined,
            gender: form.gender || undefined,
        };
        await onSave(payload);
        setSaving(false);
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="add-guest-modal">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
                <div className="flex items-center justify-between p-5 border-b">
                    <h3 className="text-lg font-bold text-slate-800">Add Guest Profile</h3>
                    <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
                </div>
                <form onSubmit={handleSubmit} className="p-5 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                        <input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                            placeholder="Guest name (optional)" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-testid="guest-name-input" />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Year *</label>
                            <input type="number" value={form.birth_year} onChange={e => setForm({...form, birth_year: e.target.value})}
                                placeholder="1990" min="1900" max="2030" required className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" data-testid="guest-birth-year" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Month *</label>
                            <select value={form.birth_month} onChange={e => setForm({...form, birth_month: e.target.value})}
                                required className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" data-testid="guest-birth-month">
                                <option value="">-</option>
                                {Array.from({length: 12}, (_, i) => <option key={i+1} value={i+1}>{i+1}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Day *</label>
                            <select value={form.birth_day} onChange={e => setForm({...form, birth_day: e.target.value})}
                                required className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" data-testid="guest-birth-day">
                                <option value="">-</option>
                                {Array.from({length: 31}, (_, i) => <option key={i+1} value={i+1}>{i+1}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Time of Birth</label>
                            <select value={form.birth_hour} onChange={e => setForm({...form, birth_hour: e.target.value})}
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" data-testid="guest-birth-hour">
                                <option value="">Unknown</option>
                                {Array.from({length: 24}, (_, i) => <option key={i} value={i}>{String(i).padStart(2,'0')}:00</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Gender</label>
                            <select value={form.gender} onChange={e => setForm({...form, gender: e.target.value})}
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" data-testid="guest-gender">
                                <option value="">Not specified</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                            </select>
                        </div>
                    </div>
                    <div className="flex gap-3 pt-2">
                        <button type="button" onClick={onClose} className="flex-1 py-2.5 border rounded-lg text-slate-700 hover:bg-slate-50 font-medium">Cancel</button>
                        <button type="submit" disabled={saving} className="flex-1 py-2.5 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg font-medium disabled:opacity-50" data-testid="save-guest-btn">
                            {saving ? 'Saving...' : 'Save Profile'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function BaziViewModal({ profile, onClose, token, onViewEnergy, onViewCalendar }) {
    const [bazi, setBazi] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchBazi = async () => {
            try {
                const res = await fetch(`${API_URL}/api/auth/admin/guest-profiles/${profile.id}/bazi`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) setBazi(await res.json());
                else toast.error('Failed to load BaZi data');
            } catch { toast.error('Network error'); }
            setLoading(false);
        };
        fetchBazi();
    }, [profile.id, token]);

    const PillarCard = ({ label, pillar }) => {
        if (!pillar) return null;
        return (
            <div className="text-center bg-white rounded-xl border-2 border-slate-200 p-3 min-w-[90px]">
                <div className="text-[11px] font-medium text-slate-500 mb-2 uppercase tracking-wider">{label}</div>
                <div className="font-chinese text-2xl font-bold text-slate-800 leading-tight">
                    {pillar.stem?.chinese}{pillar.branch?.chinese}
                </div>
                <div className="text-xs text-slate-600 mt-1">{pillar.stem?.pinyin} {pillar.branch?.pinyin}</div>
                <div className="text-xs mt-1.5 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 inline-block">
                    {pillar.branch?.animal || pillar.branch?.english}
                </div>
            </div>
        );
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="bazi-view-modal">
            <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white/90 backdrop-blur rounded-t-2xl z-10">
                    <div>
                        <h3 className="text-lg font-bold text-slate-800">{profile.name || 'Guest'}</h3>
                        <p className="text-sm text-slate-500">
                            {profile.birth_year}/{profile.birth_month}/{profile.birth_day}
                            {profile.birth_hour != null && ` at ${String(profile.birth_hour).padStart(2, '0')}:00`}
                            {profile.gender && ` · ${profile.gender}`}
                        </p>
                    </div>
                    <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
                </div>

                {loading ? (
                    <div className="p-12 text-center">
                        <div className="animate-spin w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto"></div>
                    </div>
                ) : bazi ? (
                    <div className="p-5 space-y-5">
                        {/* Birth Animal */}
                        <div className="text-center bg-amber-50 border border-amber-200 rounded-xl p-4">
                            <div className="font-chinese text-3xl mb-1">{profile.birth_animal_chinese}</div>
                            <div className="text-lg font-bold text-amber-800">{profile.birth_animal}</div>
                            <div className="text-sm text-amber-600">Birth Animal</div>
                        </div>

                        {/* Four Pillars */}
                        <div>
                            <h4 className="text-sm font-semibold text-slate-600 mb-3 uppercase tracking-wider">Four Pillars 四柱</h4>
                            <div className="grid grid-cols-4 gap-2">
                                <PillarCard label="Hour" pillar={bazi.pillars?.hour} />
                                <PillarCard label="Day" pillar={bazi.pillars?.day} />
                                <PillarCard label="Month" pillar={bazi.pillars?.month} />
                                <PillarCard label="Year" pillar={bazi.pillars?.year} />
                            </div>
                        </div>

                        {/* Day Master */}
                        {bazi.day_master && (
                            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
                                <h4 className="text-sm font-semibold text-indigo-600 mb-2 uppercase tracking-wider">Day Master 日主</h4>
                                <div className="flex items-center gap-3">
                                    <span className="font-chinese text-2xl font-bold text-indigo-800">{bazi.day_master.chinese}</span>
                                    <div>
                                        <div className="font-medium text-indigo-800">{bazi.day_master.english} ({bazi.day_master.pinyin})</div>
                                        <div className="text-sm text-indigo-600">{bazi.day_master_element?.name} element</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Elements Profile */}
                        {bazi.elements_profile && (
                            <div>
                                <h4 className="text-sm font-semibold text-slate-600 mb-3 uppercase tracking-wider">Elements Profile</h4>
                                <div className="grid grid-cols-5 gap-2">
                                    {Object.entries(bazi.elements_profile).map(([el, count]) => (
                                        <div key={el} className="text-center bg-white border rounded-lg p-2">
                                            <div className="text-lg font-bold text-slate-800">{count}</div>
                                            <div className="text-[11px] text-slate-500 capitalize">{el}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Personal Clash */}
                        {bazi.personal_clash && (
                            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                                <h4 className="text-sm font-semibold text-red-600 mb-1 uppercase tracking-wider">Personal Clash</h4>
                                <span className="font-chinese text-lg">{bazi.personal_clash.chinese}</span>
                                <span className="ml-2 text-red-800 font-medium">{bazi.personal_clash.animal}</span>
                            </div>
                        )}

                        {/* Nobleman */}
                        {bazi.nobleman_animals?.length > 0 && (
                            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                                <h4 className="text-sm font-semibold text-emerald-600 mb-1 uppercase tracking-wider">Nobleman Animals</h4>
                                <div className="text-emerald-800 font-medium">{bazi.nobleman_animals.join(', ')}</div>
                            </div>
                        )}

                        {/* Quick Links */}
                        <div className="grid grid-cols-2 gap-3 pt-2">
                            <button onClick={() => onViewEnergy(profile)}
                                className="flex items-center justify-center gap-2 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-medium transition-colors" data-testid="guest-energy-chart-link">
                                <Calendar className="w-4 h-4" />
                                Energy Chart
                            </button>
                            <button onClick={() => onViewCalendar(profile)}
                                className="flex items-center justify-center gap-2 py-3 bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl font-medium transition-colors" data-testid="guest-calendar-link">
                                <Clock className="w-4 h-4" />
                                Calendar & Timing
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="p-12 text-center text-slate-500">Failed to load BaZi data</div>
                )}
            </div>
        </div>
    );
}

const ANIMAL_TO_CHART_ID = {
    'Rat': 'x7k2m', 'Ox': 'p3n9q', 'Tiger': 'v8j4w', 'Rabbit': 'r2f6y',
    'Dragon': 'h5c1z', 'Snake': 'b9t3x', 'Horse': 'n4g8k', 'Goat': 'm1d7p',
    'Monkey': 'q6s2v', 'Rooster': 'w3h9n', 'Dog': 'y8b4j', 'Pig': 'c5r1f'
};

export default function GuestProfiles() {
    const { token, isAuthenticated, user } = useAuth();
    const navigate = useNavigate();
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [viewingProfile, setViewingProfile] = useState(null);

    const fetchProfiles = useCallback(async () => {
        if (!token) return;
        try {
            const checkRes = await fetch(`${API_URL}/api/auth/admin/check`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (checkRes.ok) {
                const checkData = await checkRes.json();
                setIsAdmin(checkData.is_admin);
                if (checkData.is_admin) {
                    const res = await fetch(`${API_URL}/api/auth/admin/guest-profiles`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (res.ok) {
                        const data = await res.json();
                        setProfiles(data.profiles);
                    }
                }
            }
        } catch (err) {
            console.error('Failed to fetch guest profiles:', err);
        }
        setLoading(false);
    }, [token]);

    useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

    const handleSave = async (payload) => {
        try {
            const res = await fetch(`${API_URL}/api/auth/admin/guest-profiles`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                toast.success('Guest profile saved');
                setShowAddModal(false);
                fetchProfiles();
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Failed to save');
            }
        } catch { toast.error('Network error'); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this guest profile?')) return;
        try {
            const res = await fetch(`${API_URL}/api/auth/admin/guest-profiles/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                toast.success('Profile deleted');
                fetchProfiles();
            }
        } catch { toast.error('Network error'); }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
                <div className="animate-spin w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full"></div>
            </div>
        );
    }

    if (!isAuthenticated || !isAdmin) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center px-4">
                <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md text-center">
                    <User className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h1 className="text-2xl font-bold text-slate-800 mb-2">Access Denied</h1>
                    <p className="text-slate-500 mb-6">Admin privileges required</p>
                    <button onClick={() => navigate('/')} className="bg-slate-600 hover:bg-slate-700 text-white font-semibold py-3 px-6 rounded-lg">Go Home</button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4" data-testid="guest-profiles-page">
            <div className="max-w-5xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-4">
                        <button onClick={() => navigate('/admin-dashboard')} className="p-2 hover:bg-white rounded-lg transition-colors" data-testid="back-to-admin">
                            <ArrowLeft className="w-5 h-5 text-slate-600" />
                        </button>
                        <div>
                            <h1 className="text-2xl font-bold text-slate-800">Guest Profiles</h1>
                            <p className="text-slate-500">Check energy chart, calendar & timing for non-registered users</p>
                        </div>
                    </div>
                    <button onClick={() => setShowAddModal(true)}
                        className="flex items-center gap-2 px-4 py-2.5 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg font-medium transition-colors"
                        data-testid="add-guest-btn">
                        <Plus className="w-4 h-4" />
                        Add Guest
                    </button>
                </div>

                {/* Profiles Grid */}
                {profiles.length === 0 ? (
                    <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                        <User className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-slate-600 mb-2">No Guest Profiles Yet</h3>
                        <p className="text-slate-400 mb-6">Add a guest's date of birth to check their energy chart and timing</p>
                        <button onClick={() => setShowAddModal(true)}
                            className="px-6 py-2.5 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg font-medium">
                            Add First Guest
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {profiles.map(p => (
                            <div key={p.id} className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden hover:shadow-xl transition-shadow" data-testid={`guest-card-${p.id}`}>
                                <div className="p-5">
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                                                <span className="font-chinese text-xl text-indigo-700">{p.birth_animal_chinese || '?'}</span>
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-slate-800">{p.name || 'Guest'}</h3>
                                                <span className="text-sm text-indigo-600 font-medium">{p.birth_animal}</span>
                                            </div>
                                        </div>
                                        <button onClick={() => handleDelete(p.id)}
                                            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                            data-testid={`delete-guest-${p.id}`}>
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                    <div className="space-y-1.5 text-sm text-slate-600 mb-4">
                                        <div className="flex items-center gap-2">
                                            <Calendar className="w-3.5 h-3.5 text-slate-400" />
                                            <span>{p.birth_year}/{p.birth_month}/{p.birth_day}</span>
                                        </div>
                                        {p.birth_hour != null && (
                                            <div className="flex items-center gap-2">
                                                <Clock className="w-3.5 h-3.5 text-slate-400" />
                                                <span>{String(p.birth_hour).padStart(2, '0')}:00</span>
                                            </div>
                                        )}
                                        {p.gender && (
                                            <div className="flex items-center gap-2">
                                                <User className="w-3.5 h-3.5 text-slate-400" />
                                                <span className="capitalize">{p.gender}</span>
                                            </div>
                                        )}
                                    </div>
                                    <button onClick={() => setViewingProfile(p)}
                                        className="w-full flex items-center justify-center gap-2 py-2.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-xl font-medium transition-colors"
                                        data-testid={`view-guest-${p.id}`}>
                                        <Eye className="w-4 h-4" />
                                        View BaZi & Charts
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {showAddModal && <AddGuestModal onClose={() => setShowAddModal(false)} onSave={handleSave} />}
            {viewingProfile && (
                <BaziViewModal
                    profile={viewingProfile}
                    onClose={() => setViewingProfile(null)}
                    token={token}
                    onViewEnergy={(p) => {
                        const chartId = ANIMAL_TO_CHART_ID[p.birth_animal];
                        if (chartId) {
                            const guestName = encodeURIComponent(p.name || 'Guest');
                            window.open(`/energy-chart/${chartId}?guest_name=${guestName}`, '_blank');
                        }
                    }}
                    onViewCalendar={(p) => {
                        const guestName = encodeURIComponent(p.name || 'Guest');
                        const params = `guest_animal=${p.birth_animal}&guest_animal_index=${p.birth_animal_index}&guest_animal_chinese=${encodeURIComponent(p.birth_animal_chinese || '')}&guest_name=${guestName}`;
                        window.open(`/calendar?${params}`, '_blank');
                    }}
                />
            )}
        </div>
    );
}
